import { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import usePageSeo from "@/hooks/usePageSeo";
import { articles, categories } from "@/mocks/gua";

const tabs = [{ id: "all", name: "全部合集" }, ...categories.map((c) => ({ id: c.id, name: c.name }))];

export default function Collections() {
  const [params, setParams] = useSearchParams();
  const activeCat = params.get("cat") || "all";
  const sort = params.get("sort") || "new";
  const urlQ = params.get("q") || "";
  const [keyword, setKeyword] = useState(urlQ);

  useEffect(() => {
    setKeyword(urlQ);
  }, [urlQ]);

  usePageSeo({
    title: "合集总览 | 蝌蚪吃瓜补档与考古合集",
    description:
      "浏览蝌蚪吃瓜全部合集：考古旧瓜、名场面补档、圈内合集与实时热议，支持分类筛选与关键词搜索，错过的瓜一次翻齐。",
    keywords: "蝌蚪吃瓜合集,蝌蚪吃瓜补档,考古旧瓜,名场面补档,圈内合集",
    canonical: "/heji",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "合集总览 - 蝌蚪吃瓜",
        url: "https://cndonglong.cn/heji",
        inLanguage: "zh-CN",
        description: "蝌蚪吃瓜全部合集列表，支持按分类与关键词浏览。",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://cndonglong.cn/" },
          { "@type": "ListItem", position: 2, name: "合集总览", item: "https://cndonglong.cn/heji" },
        ],
      },
    ],
  });

  const update = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (!value || value === "all" || (key === "sort" && value === "new")) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setParams(next, { replace: false });
  };

  const list = useMemo(() => {
    const kw = urlQ.trim().toLowerCase();
    const filtered = articles.filter((a) => {
      const matchCat = activeCat === "all" || a.category === activeCat;
      const matchKw =
        !kw ||
        a.title.toLowerCase().includes(kw) ||
        a.summary.toLowerCase().includes(kw) ||
        a.tags.some((t) => t.toLowerCase().includes(kw));
      return matchCat && matchKw;
    });
    if (sort === "hot") return [...filtered].sort((a, b) => b.hot - a.hot);
    if (sort === "read") return [...filtered].sort((a, b) => b.readCount - a.readCount);
    return [...filtered].sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [activeCat, urlQ, sort]);

  const currentCat = categories.find((c) => c.id === activeCat);

  return (
    <div className="bg-background-50">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "合集总览", to: currentCat ? "/heji" : undefined },
              ...(currentCat ? [{ label: currentCat.name }] : []),
            ]}
          />
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
            {currentCat ? currentCat.name : "全部合集总览"}
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            {currentCat
              ? currentCat.desc
              : "把零散的消息整理成有脉络的合集，支持按分类筛选、关键词搜索与热度排序，错过的瓜这里都能翻到。"}
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-[1280px] px-4 py-10 md:px-6">
        <div className="flex flex-col gap-4 rounded-xl border border-background-200 bg-background-50 p-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => update("cat", tab.id)}
                className={`rounded-full px-4 py-2 text-sm transition-colors whitespace-nowrap ${
                  activeCat === tab.id
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {tab.name}
              </button>
            ))}
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                update("q", keyword.trim());
              }}
              className="flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-2"
            >
              <i className="ri-search-line text-base text-foreground-500"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索合集或话题"
                className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none sm:w-44"
              />
              {urlQ && (
                <button
                  type="button"
                  aria-label="清除搜索"
                  onClick={() => {
                    setKeyword("");
                    update("q", "");
                  }}
                  className="flex h-5 w-5 items-center justify-center rounded-full bg-background-300 text-foreground-700"
                >
                  <i className="ri-close-line text-xs"></i>
                </button>
              )}
            </form>

            <div className="flex items-center gap-1 rounded-full bg-background-100 px-1 py-1">
              {[
                { id: "new", label: "最新" },
                { id: "hot", label: "最热" },
                { id: "read", label: "回看最多" },
              ].map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => update("sort", s.id)}
                  className={`rounded-full px-3 py-1.5 text-xs transition-colors whitespace-nowrap ${
                    sort === s.id
                      ? "bg-secondary-500 text-background-50"
                      : "text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <p className="mt-5 text-sm text-foreground-500">
          共找到 <span className="font-medium text-foreground-900">{list.length}</span> 个合集
          {urlQ && <span> · 关键词「{urlQ}」</span>}
        </p>

        {list.length > 0 ? (
          <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((a) => (
              <ArticleCard key={a.id} article={a} />
            ))}
          </div>
        ) : (
          <div className="mt-5 flex flex-col items-center justify-center rounded-xl border border-dashed border-background-300 bg-background-100 py-20 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-search-eye-line text-2xl"></i>
            </span>
            <h3 className="mt-4 font-heading text-lg font-bold text-foreground-900">
              没有找到相关合集
            </h3>
            <p className="mt-2 text-sm text-foreground-500">
              换个关键词试试，或到「联系我们」推荐一个值得考古的话题。
            </p>
          </div>
        )}
      </div>
    </div>
  );
}